import { titles, type MediaType, type Title } from "@/mocks/titles";

export interface CategoryInfo {
  type: MediaType;
  name: string;
  englishName: string;
  description: string;
  longDescription: string;
  genres: string[];
  heroImage: string;
}

export const movieCategory: CategoryInfo = {
  type: "movie",
  name: "电影",
  englishName: "Movies",
  description: "从院线新片到影史经典，按类型、地区与年份地毯式搜片。",
  longDescription:
    "电影板块收录剧情、悬疑、科幻、爱情、动作、家庭等多个类型的长片作品。这里既有正在热映的口碑新作，也有值得反复回看的经典老片，你可以按热门、最新或评分重新排列，快速找到今晚想看的那一部。",
  genres: ["全部", "剧情", "悬疑", "科幻", "爱情", "动作", "奇幻", "家庭", "青春", "文艺", "西部"],
  heroImage:
    "https://readdy.ai/api/search-image?query=cinematic%20dark%20movie%20theater%20interior%20with%20empty%20navy%20velvet%20seats%20and%20warm%20amber%20screen%20glow%20atmospheric%20wide%20shot%20no%20text&width=1920&height=760&seq=cat-movie-hero&orientation=landscape",
};

export const tvCategory: CategoryInfo = {
  type: "tv",
  name: "电视剧",
  englishName: "TV Series",
  description: "国产剧、韩剧、美剧、日剧一网打尽，追剧不再迷路。",
  longDescription:
    "电视剧板块覆盖悬疑罪案、古装年代、都市职场、科幻喜剧与生活纪实等题材。每一部作品都标注了集数与单片时长，方便你按自己的时间安排挑选。想找下饭剧，还是想找一部能熬夜的悬疑剧，这里都有。",
  genres: ["全部", "悬疑", "罪案", "古装", "年代", "都市", "职场", "家庭", "科幻", "生活", "爱情", "文艺", "纪实", "喜剧"],
  heroImage:
    "https://readdy.ai/api/search-image?query=warm%20living%20room%20at%20night%20with%20a%20glowing%20television%20screen%20and%20deep%20navy%20walls%20amber%20lamp%20light%20cozy%20cinematic%20wide%20shot%20no%20text&width=1920&height=760&seq=cat-tv-hero&orientation=landscape",
};

export function getCategoryConfig(type?: string): CategoryInfo | null {
  if (type === "movie") return movieCategory;
  if (type === "tv") return tvCategory;
  return null;
}

export const allRegions = ["全部", "中国内地", "中国香港", "中国台湾", "美国", "韩国", "日本", "英国", "法国", "印度", "泰国"];

export const allYears = ["全部", "2025", "2024", "2023", "2022", "2021"];

export type SortKey = "heat" | "latest" | "rating";

export const sortOptions: { key: SortKey; label: string }[] = [
  { key: "heat", label: "按热门" },
  { key: "latest", label: "按最新" },
  { key: "rating", label: "按评分" },
];

export interface QueryOptions {
  type?: MediaType;
  genre?: string;
  region?: string;
  year?: string;
  keyword?: string;
}

export function filterTitles(options: QueryOptions): Title[] {
  const { type, genre, region, year, keyword } = options;
  const kw = keyword?.trim().toLowerCase();
  return titles.filter((item) => {
    if (type && item.type !== type) return false;
    if (genre && genre !== "全部" && !item.genres.includes(genre)) return false;
    if (region && region !== "全部" && item.region !== region) return false;
    if (year && year !== "全部" && String(item.year) !== year) return false;
    if (kw) {
      const haystack = `${item.title} ${item.englishTitle} ${item.director} ${item.cast.join(" ")} ${item.genres.join(" ")}`.toLowerCase();
      if (!haystack.includes(kw)) return false;
    }
    return true;
  });
}

export function sortTitles(list: Title[], sort: SortKey): Title[] {
  const copy = [...list];
  if (sort === "latest") {
    copy.sort((a, b) => b.year - a.year || b.heat - a.heat);
  } else if (sort === "rating") {
    copy.sort((a, b) => b.rating - a.rating || b.heat - a.heat);
  } else {
    copy.sort((a, b) => b.heat - a.heat);
  }
  return copy;
}

export function getTitleById(id?: string): Title | undefined {
  return titles.find((item) => item.id === id);
}

export function getRelatedTitles(target: Title, limit = 8): Title[] {
  return titles
    .filter((item) => item.id !== target.id)
    .map((item) => {
      let score = 0;
      if (item.type === target.type) score += 3;
      if (item.director === target.director) score += 5;
      score += item.genres.filter((g) => target.genres.includes(g)).length * 2;
      if (item.region === target.region) score += 1;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.heat - a.item.heat)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function formatHeat(heat: number): string {
  if (heat >= 10000) return `${(heat / 1000).toFixed(1)}k`;
  return String(heat);
}